import type { ChangeSummary, Decision } from "@szyos/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { api, errorMessage } from "../api";
import { dateTime, shortHash } from "../format";
import { Icon } from "../icons";
import { ErrorNote, useToast } from "./feedback";
import { HoldButton } from "./HoldButton";

const CATEGORY_LABEL: Record<Decision["category"], string> = {
  product: "Produkt",
  design: "Design",
  architecture: "Architektur",
  data_sharing: "Datenfreigabe",
  security: "Sicherheit",
  budget: "Budget",
  review: "Review",
  release: "Auslieferung",
};

const CATEGORY_ICON: Record<Decision["category"], string> = {
  product: "idea",
  design: "palette",
  architecture: "network",
  data_sharing: "lock",
  security: "shield",
  budget: "alert",
  review: "pr",
  release: "rocket",
};

/** Stichwortübersicht: was der Pull Request neu anlegt, ändert, umbenennt oder löscht, dazu die Aufgaben. */
function ChangeList({ changes }: { changes: ChangeSummary }) {
  const groups = [
    { label: "Neu", icon: "plus", files: changes.added },
    { label: "Geändert", icon: "edit", files: changes.modified },
    { label: "Umbenannt", icon: "arrow", files: changes.renamed },
    { label: "Gelöscht", icon: "trash", files: changes.removed },
  ].filter((g) => g.files.length > 0);
  return (
    <section className="dec-changes" aria-label="Was sich ändert">
      <h3>Was sich ändert</h3>
      <p className="muted">
        {changes.files === 0
          ? "Die Dateien des Pull Requests waren gerade nicht abrufbar."
          : `${changes.files} ${changes.files === 1 ? "Datei" : "Dateien"}, +${changes.additions} / −${changes.deletions} Zeilen${changes.truncated ? ", Liste gekürzt" : ""}`}
      </p>
      {changes.tasks.length > 0 && (
        <details open>
          <summary>Erledigte Aufgaben ({changes.tasks.length})</summary>
          <ul>
            {changes.tasks.map((t, i) => (
              <li key={`${i}-${t}`}>{t}</li>
            ))}
          </ul>
        </details>
      )}
      {groups.map((g) => (
        <details key={g.label} open={g.files.length <= 8}>
          <summary>
            <Icon name={g.icon} /> {g.label} ({g.files.length})
          </summary>
          <ul className="dec-files">
            {g.files.map((f) => (
              <li key={f}>
                <code>{f}</code>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </section>
  );
}

/**
 * Entscheidungskarte (docs/16 §6): Frage, Bindung, Optionen mit Vor- und Nachteilen.
 * Gesendet wird die Bindung, die der Mensch gesehen hat; der Server prüft sie gegen den aktuellen Stand.
 */
export function DecisionCard({ decision, compact = false }: { decision: Decision; compact?: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState(decision.recommendedOptionId ?? decision.options[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  const [checked, setChecked] = useState<number[]>([]);
  const option = decision.options.find((o) => o.id === selected) ?? decision.options[0];
  const steps = decision.checklist;
  // Auslieferung von Hand: „Ausgeliefert“ erst, wenn jeder Schritt abgehakt ist (der Server prüft das ebenso).
  const missing = option?.id === "delivered" ? steps.length - checked.length : 0;

  const resolve = useMutation({
    mutationFn: () =>
      api.resolveDecision(decision.id, selected, decision.binding, steps.length > 0 ? checked : undefined),
    onSuccess: async () => {
      setError(null);
      toast(`Entscheidung gespeichert: ${option?.title ?? ""}`);
      await queryClient.invalidateQueries();
    },
    onError: (err) => {
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });

  if (decision.state !== "PENDING") {
    return (
      <div className="dec-done" role="status">
        <span className="big-check">
          <Icon name="check" />
        </span>
        <strong>
          {decision.state === "RESOLVED"
            ? "Entschieden"
            : decision.state === "EXPIRED"
              ? "Abgelaufen"
              : "Verfallen"}
        </strong>
        <span>{decision.question}</span>
      </div>
    );
  }

  const onKey = (e: React.KeyboardEvent, index: number) => {
    if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
      const next = decision.options[(index + step + decision.options.length) % decision.options.length];
      if (next) setSelected(next.id);
    }
  };

  return (
    <div className={`decision-body${compact ? " is-compact" : ""}`}>
      <div className="dec-top">
        <span className="dec-kicker">Deine Entscheidung</span>
        {decision.blocks && (
          <span className="badge-block">
            <Icon name="alert" /> Blockiert {decision.blocks}
          </span>
        )}
      </div>
      <h2 className="dec-title">
        <Icon name={CATEGORY_ICON[decision.category]} />
        <span>{decision.question}</span>
      </h2>
      {decision.context && <p className="dec-text">{decision.context}</p>}
      {decision.changes && <ChangeList changes={decision.changes} />}
      <ul className="binding" aria-label="Gebunden an">
        <li>
          <Icon name="file" /> Spec {shortHash(decision.binding.specHash)}
        </li>
        <li>
          <Icon name="network" /> Plan {shortHash(decision.binding.planHash)}
        </li>
        <li>
          <Icon name="lock" /> Rolle {decision.requiredRole}
        </li>
        <li>
          <Icon name="alert" /> {CATEGORY_LABEL[decision.category]}
        </li>
      </ul>
      {steps.length > 0 && (
        <fieldset className="dec-checklist">
          <legend>
            Schritte abhaken ({checked.length} von {steps.length})
          </legend>
          <ul>
            {steps.map((step, i) => (
              <li key={`${i}-${step}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={checked.includes(i)}
                    onChange={() =>
                      setChecked((c) =>
                        c.includes(i) ? c.filter((x) => x !== i) : [...c, i].sort((a, b) => a - b),
                      )
                    }
                  />{" "}
                  {step}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}
      <div className="options" role="radiogroup" aria-label="Optionen">
        {decision.options.map((o, i) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={o.id === selected}
            tabIndex={o.id === selected ? 0 : -1}
            className="option"
            data-tone={i === 0 ? "go" : "alt"}
            onClick={() => setSelected(o.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span>
              <span className="opt-label">
                {o.label}
                {decision.recommendedOptionId === o.id ? " · empfohlen" : ""}
                {o.id === selected && (
                  <span className="opt-selected">
                    <Icon name="check" /> Ausgewählt
                  </span>
                )}
              </span>
              <span className="opt-title">{o.title}</span>
            </span>
            <ul className="opt-list">
              {o.pros.map((t) => (
                <li key={`p${t}`} className="ok">
                  <Icon name="check" />
                  <span>{t}</span>
                </li>
              ))}
              {o.cons.map((t) => (
                <li key={`c${t}`} className="con">
                  <Icon name="x" />
                  <span>{t}</span>
                </li>
              ))}
              {o.notes.map((t) => (
                <li key={`n${t}`} className="info">
                  <Icon name="alert" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>
      <ErrorNote error={error} />
      <div className="dec-actions">
        <div>
          {option?.requiresHold ? (
            <HoldButton
              describedBy={noteId}
              disabled={resolve.isPending}
              onConfirm={() => resolve.mutate()}
              onTooShort={() => toast("Sicherheitsfreigabe: Taste oder Maus gedrückt halten.")}
            >
              Halten &amp; freigeben
            </HoldButton>
          ) : (
            <button
              type="button"
              className="btn btn--primary"
              disabled={resolve.isPending || missing > 0}
              aria-describedby={noteId}
              onClick={() => resolve.mutate()}
            >
              <span className="btn-label">
                {resolve.isPending ? "Wird gespeichert …" : `${option?.label ?? "Auswahl"} bestätigen`}{" "}
                <Icon name="arrow" />
              </span>
            </button>
          )}
          <small id={noteId}>
            {missing > 0 ? `Noch ${missing === 1 ? "ein Schritt" : `${missing} Schritte`} abzuhaken. ` : ""}
            gültig bis {dateTime(decision.expiresAt)}
          </small>
        </div>
        <div>
          <span className="dec-choice">
            Gewählt: <strong>{option?.title}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}
